import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Link as ChakraLink,
  FormControl,
  FormLabel,
  Heading,
  Input,
  List,
  ListItem,
  Select,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { theme } from '@resistapp/client/components/shared/theme';
import {
  EnvironmentSubType,
  EnvironmentType,
  friendlyEnvSubType,
  isLegalSubtype,
} from '@resistapp/common/environment-types';
import { values } from 'lodash';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useUser } from '../../contexts/use-user-context';
import { EnvironmentCombineParams, useCheckEnvironmentCombine, useCombineEnvironments } from '../../hooks/api';

export function CombineEnvironmentsView() {
  const toast = useToast();
  const { isAdmin } = useUser();
  const [params, setParams] = useState<EnvironmentCombineParams>({
    name: '',
    type: '',
    subtype: null,
  });
  const [checkResult, setCheckResult] = useState<{
    affectedProjects: Array<{ id: number; name: string; sampleCount: number }>;
    canCombine: boolean;
    message?: string;
  } | null>(null);

  const checkMutation = useCheckEnvironmentCombine();
  const combineMutation = useCombineEnvironments();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setParams(prev => ({
      ...prev,
      [name]: value === '' ? (name === 'subtype' ? null : '') : value,
    }));

    // Reset check result when parameters change
    if (checkResult) {
      setCheckResult(null);
    }
  };

  // Get valid subtypes for the selected environment type
  const validSubtypes = useMemo(() => {
    if (!params.type) return [];

    const selectedType = params.type as EnvironmentType;
    return values(EnvironmentSubType)
      .filter(subtype => isLegalSubtype(subtype, selectedType))
      .map(subtype => ({
        value: subtype,
        label: friendlyEnvSubType(subtype, selectedType),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [params.type]);

  const handleCheck = () => {
    checkMutation.mutate(params, {
      onSuccess: result => {
        setCheckResult(result);

        if (!result.canCombine) {
          toast({
            title: 'Cannot combine environments',
            description: result.message,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        } else if (result.affectedProjects.length === 0) {
          toast({
            title: 'No projects affected',
            description: 'No projects will be affected by this combination',
            status: 'info',
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'Check completed',
            description: `Found ${result.affectedProjects.length} affected projects with duplicate environments`,
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
        }
      },
      onError: (error: Error) => {
        setCheckResult({
          affectedProjects: [],
          canCombine: false,
          message: error.message || 'An error occurred while checking environments',
        });
      },
    });
  };

  const handleCombine = () => {
    if (!checkResult?.canCombine) {
      return;
    }

    combineMutation.mutate(params, {
      onSuccess: result => {
        if (result.canCombine) {
          toast({
            title: 'Environments combined successfully',
            description: `Combined environments for ${result.affectedProjects.length} projects`,
            status: 'success',
            duration: 5000,
            isClosable: true,
          });

          // Reset the form and check result
          setParams({ name: '', type: '', subtype: null });
          setCheckResult(null);
        } else {
          toast({
            title: 'Failed to combine environments',
            description: result.message || 'An error occurred during combination',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      },
      onError: (error: Error) => {
        toast({
          title: 'Error combining environments',
          description: error.message || 'An error occurred while combining environments',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    });
  };

  const handleButtonClick = () => {
    if (checkResult?.canCombine) {
      handleCombine();
    } else {
      handleCheck();
    }
  };

  if (!isAdmin) {
    return (
      <Box p={8}>
        <Text>You must be an admin to access this page.</Text>
      </Box>
    );
  }

  return (
    <Box maxW="container.md" mx="auto" p={5}>
      <Heading as="h2" size="lg" mb={4}>
        Combine Duplicate Environments
      </Heading>
      <Text mb={6}>
        Use this tool to merge duplicate microbiome entries so that platform can recognize and link old and new samples
        that represent the same site and sampled microbiobial environment. Please review that all associated samples
        belong to the same customer organisation. This action cannot be undone.
      </Text>

      <VStack spacing={4} mb={6} as="form">
        <FormControl isRequired>
          <FormLabel>Environment Name</FormLabel>
          <Input
            name="name"
            value={params.name}
            onChange={handleInputChange}
            placeholder="Enter exact environment name"
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>Environment Type</FormLabel>
          <Select name="type" value={params.type} onChange={handleInputChange} placeholder="Select environment type">
            {Object.values(EnvironmentType).map(type => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel>Environment Subtype (Optional)</FormLabel>
          <Select
            name="subtype"
            value={params.subtype || ''}
            onChange={handleInputChange}
            placeholder="Select environment subtype or leave empty"
            isDisabled={!params.type || validSubtypes.length === 0}
          >
            <option value="">None</option>
            {validSubtypes.map(subtype => (
              <option key={subtype.value} value={subtype.value}>
                {subtype.label}
              </option>
            ))}
          </Select>
        </FormControl>

        <Box alignSelf="flex-start">
          <Button
            colorScheme={checkResult?.canCombine ? 'green' : 'blue'}
            onClick={handleButtonClick}
            isLoading={checkMutation.isPending || combineMutation.isPending}
            isDisabled={!params.name || !params.type}
          >
            {checkResult?.canCombine ? 'Combine' : 'Check'}
          </Button>
        </Box>
      </VStack>

      {checkResult && (
        <Box mt={6} p={4} borderWidth="1px" borderRadius="md">
          <Heading as="h3" size="md" mb={4}>
            Check Results
          </Heading>

          {checkResult.message && !checkResult.canCombine && (
            <Alert status="error" mb={4}>
              <AlertIcon />
              <AlertDescription>{checkResult.message}</AlertDescription>
            </Alert>
          )}

          {checkResult.affectedProjects.length > 0 ? (
            <>
              <Text mb={4}>
                {checkResult.canCombine
                  ? 'The following projects will be affected by this environment combination:'
                  : 'Found affected projects, but cannot combine (see error above).'}
              </Text>
              <List spacing={2}>
                {checkResult.affectedProjects.map(project => (
                  <ListItem key={project.id}>
                    <ChakraLink as={RouterLink} to={`/research/${project.id}`} color={theme.colors.blue500}>
                      {project.name}
                    </ChakraLink>{' '}
                    ({project.sampleCount} sample(s))
                  </ListItem>
                ))}
              </List>
              {checkResult.canCombine && (
                <Text mt={4} fontStyle="italic">
                  Review the projects above. If correct, press &quot;Combine&quot;. This action cannot be undone.
                </Text>
              )}
            </>
          ) : (
            <Text>No projects found with duplicate environments matching the criteria.</Text>
          )}
        </Box>
      )}
    </Box>
  );
}
